import React, { useState } from "react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip,
  RadarChart, PolarGrid, PolarAngleAxis, Radar,
} from "recharts";
import { Bot, ArrowRight, Check, Clock, Gauge, Target as TargetIcon, TrendingUp } from "lucide-react";
import { Reveal, CountUp } from "@/components/Reveal";
import { COACH_DEMO, MISTAKES, TRAJECTORY, PERF_METRICS, RADAR_DATA } from "@/data/mock";
import { useStartFlow } from "@/lib/useStart";

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-white/10 bg-black/80 backdrop-blur-xl px-3 py-2 text-xs text-white">
      <p className="text-white/60">{label}</p>
      <p className="font-semibold text-amber-300">{payload[0].value} marks</p>
    </div>
  );
};

/* ---------------- AI Coach ---------------- */
const AICoach = () => (
  <div className="grid lg:grid-cols-2 gap-12 items-center">
    <Reveal>
      <p className="text-xs uppercase tracking-[0.2em] text-amber-300 font-semibold mb-4">The AI coach</p>
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
        Not another chatbot.
        <br />
        <span className="text-gradient">An AI that knows your preparation.</span>
      </h2>
      <p className="mt-5 text-white/60 leading-relaxed">
        sarkarijobai's coach uses your actual mock scores, weak topics and available time to make specific,
        defensible decisions — not generic advice.
      </p>
    </Reveal>

    <Reveal delay={0.12}>
      <div className="rounded-3xl border border-white/10 bg-[#0a0a0c] p-5 shadow-2xl">
        <div className="flex items-center gap-2 pb-4 mb-4 border-b border-white/10">
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-amber-400 to-red-600 flex items-center justify-center">
            <Bot className="h-4 w-4 text-white" />
          </div>
          <span className="text-sm font-semibold text-white">sarkarijobai Coach</span>
          <span className="ml-auto flex items-center gap-1.5 text-[11px] text-emerald-400">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> using your data
          </span>
        </div>
        <div className="space-y-3">
          {COACH_DEMO.map((m) => (
            <div key={`${m.role}-${m.text.slice(0, 24)}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                  m.role === "user"
                    ? "bg-white/10 text-white rounded-br-sm"
                    : "bg-gradient-to-br from-amber-500/15 to-red-600/10 border border-amber-400/20 text-white/90 rounded-bl-sm"
                }`}
              >
                {m.text}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Reveal>
  </div>
);

/* ---------------- Performance Intelligence ---------------- */
const stat = [
  { label: "Current score", value: 142, icon: Gauge },
  { label: "Target", value: "180+", icon: TargetIcon },
  { label: "Est. percentile", value: 96.8, decimals: 1, icon: TrendingUp },
  { label: "Biggest opportunity", value: "Maths", icon: TargetIcon },
];

const Performance = () => (
  <div>
    <Reveal className="max-w-2xl mb-12">
      <p className="text-xs uppercase tracking-[0.2em] text-amber-300 font-semibold mb-4">Performance intelligence</p>
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
        Know where you're losing marks.
      </h2>
    </Reveal>

    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {stat.map((s, i) => (
        <Reveal key={s.label} delay={i * 0.06}>
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
            <p className="text-[11px] uppercase tracking-wide text-white/40 mb-2">{s.label}</p>
            <p className="font-display text-3xl font-bold text-white">
              {typeof s.value === "number" ? <CountUp to={s.value} decimals={s.decimals || 0} /> : s.value}
            </p>
          </div>
        </Reveal>
      ))}
    </div>

    <div className="grid lg:grid-cols-2 gap-6">
      <Reveal>
        <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-6">
          <p className="text-sm font-semibold text-white mb-6">Skill breakdown</p>
          <div className="space-y-5">
            {PERF_METRICS.map((m) => (
              <div key={m.label}>
                <div className="flex justify-between text-xs mb-1.5">
                  <span className="text-white/60">{m.label}</span>
                  <span className="text-amber-300 font-medium">{m.value}{m.suffix}</span>
                </div>
                <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-red-500" style={{ width: `${m.value}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-6">
          <p className="text-sm font-semibold text-white mb-2">Topic mastery vs target</p>
          <div className="h-[260px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={RADAR_DATA} outerRadius="70%">
                <PolarGrid stroke="rgba(255,255,255,0.1)" />
                <PolarAngleAxis dataKey="topic" tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 11 }} />
                <Radar dataKey="target" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.08} />
                <Radar dataKey="you" stroke="#00f0ff" fill="#00f0ff" fillOpacity={0.28} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Reveal>
    </div>
  </div>
);

/* ---------------- Mistake Bank ---------------- */
const MistakeBank = () => {
  const start = useStartFlow();
  return (
    <div className="grid lg:grid-cols-2 gap-12 items-center">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.2em] text-amber-300 font-semibold mb-4">Mistake bank</p>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
          Turn mistakes into progress.
        </h2>
        <div className="mt-6 rounded-2xl border border-amber-400/20 bg-amber-500/[0.06] p-5">
          <div className="flex items-start gap-3">
            <Bot className="h-5 w-5 text-amber-300 shrink-0 mt-0.5" />
            <p className="text-sm text-white/80 leading-relaxed">
              "You have repeated this error 3 times. Let's fix the underlying concept."
            </p>
          </div>
        </div>
        <button
          onClick={() => start("mistake-bank")}
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400 to-red-600 text-white text-sm font-medium px-6 py-3 hover:scale-105 transition-transform"
          data-testid="mistake-bank-cta"
        >
          Fix My Mistakes <ArrowRight className="h-4 w-4" />
        </button>
      </Reveal>

      <Reveal delay={0.12}>
        <div className="space-y-3">
          {MISTAKES.map((m) => (
            <div key={m.title} className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-5">
              <div className="h-10 w-10 rounded-xl bg-red-500/15 flex items-center justify-center shrink-0">
                <span className="font-display text-sm font-bold text-red-400">{m.count}×</span>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-white">{m.title}</p>
                <p className="text-xs text-white/40">{m.count} repeated mistakes</p>
              </div>
              <span className="text-xs font-medium text-amber-300">Fix →</span>
            </div>
          ))}
        </div>
      </Reveal>
    </div>
  );
};

/* ---------------- Adaptive Practice ---------------- */
const AdaptivePractice = () => {
  const [answered, setAnswered] = useState(false);
  return (
    <div className="grid lg:grid-cols-2 gap-12 items-center">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.2em] text-amber-300 font-semibold mb-4">Adaptive practice</p>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
          Every question has a purpose.
        </h2>
        <p className="mt-5 text-white/60 leading-relaxed">
          Questions are chosen because of your weaknesses — and every attempt is measured on accuracy,
          speed and confidence, not just right or wrong.
        </p>
      </Reveal>

      <Reveal delay={0.12}>
        <div className="rounded-3xl border border-white/10 bg-[#0a0a0c] p-6">
          <span className="inline-block text-[10px] font-semibold uppercase tracking-wide text-amber-300 bg-amber-400/15 px-2.5 py-1 rounded-full">
            Recommended for you
          </span>
          <p className="text-xs text-white/40 mt-3">Your conceptual accuracy in this topic is currently 43%.</p>
          <p className="text-white font-medium mt-4 leading-relaxed">
            A uniform rod of mass M and length L rotates about one end. What is its moment of inertia?
          </p>
          <div className="mt-4 space-y-2">
            {["ML²/12", "ML²/3", "ML²/2", "ML²"].map((opt, i) => (
              <button
                key={opt}
                onClick={() => setAnswered(true)}
                className={`w-full text-left text-sm rounded-xl border px-4 py-2.5 transition-colors ${
                  answered && i === 1
                    ? "border-emerald-400/50 bg-emerald-400/10 text-emerald-300"
                    : "border-white/10 bg-white/[0.02] text-white/80 hover:border-amber-400/30"
                }`}
                data-testid={`practice-option-${i}`}
              >
                {opt}
              </button>
            ))}
          </div>
          {answered && (
            <div className="mt-4 grid grid-cols-3 gap-3 text-center">
              <div className="rounded-xl bg-white/[0.03] py-2.5">
                <p className="text-[10px] text-white/40">Result</p>
                <p className="text-sm font-semibold text-emerald-400 flex items-center justify-center gap-1"><Check className="h-3.5 w-3.5" /> Correct</p>
              </div>
              <div className="rounded-xl bg-white/[0.03] py-2.5">
                <p className="text-[10px] text-white/40">Time</p>
                <p className="text-sm font-semibold text-white flex items-center justify-center gap-1"><Clock className="h-3.5 w-3.5" /> 1:52</p>
              </div>
              <div className="rounded-xl bg-white/[0.03] py-2.5">
                <p className="text-[10px] text-white/40">Confidence</p>
                <p className="text-sm font-semibold text-white">Medium</p>
              </div>
            </div>
          )}
          {answered && (
            <p className="mt-4 text-xs text-amber-200 bg-amber-400/10 border border-amber-400/20 rounded-xl px-4 py-3 leading-relaxed">
              "Your answer was correct, but your solving time is above your target."
            </p>
          )}
        </div>
      </Reveal>
    </div>
  );
};

/* ---------------- Score Trajectory ---------------- */
const ScoreTrajectory = () => (
  <div>
    <Reveal className="max-w-2xl mb-10">
      <p className="text-xs uppercase tracking-[0.2em] text-amber-300 font-semibold mb-4">Score trajectory</p>
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
        Don't just study. Know if you're moving forward.
      </h2>
    </Reveal>

    <Reveal delay={0.1}>
      <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-6">
        <div className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={TRAJECTORY} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="scoreFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00f0ff" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#00f0ff" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="name" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 12 }} axisLine={false} tickLine={false} domain={[100, 200]} />
              <Tooltip content={<ChartTooltip />} />
              <Area type="monotone" dataKey="score" stroke="#00f0ff" strokeWidth={2.5} fill="url(#scoreFill)" dot={{ fill: "#00f0ff", r: 4 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-white/10">
          {[
            { l: "Current trajectory", v: "On track", c: "text-emerald-400" },
            { l: "Target trajectory", v: "180+ by exam", c: "text-white" },
            { l: "Highest-risk subject", v: "Chemistry", c: "text-amber-400" },
            { l: "Recommended intervention", v: "Daily revision", c: "text-amber-300" },
          ].map((x) => (
            <div key={x.l}>
              <p className="text-[11px] text-white/40">{x.l}</p>
              <p className={`text-sm font-semibold ${x.c}`}>{x.v}</p>
            </div>
          ))}
        </div>
        <p className="mt-5 text-[11px] text-white/35 leading-relaxed">
          Performance projections are estimates based on available data and are not guarantees of exam outcomes.
        </p>
      </div>
    </Reveal>
  </div>
);

export const ProductSections = () => (
  <section id="features" className="bg-[#040406] py-24 md:py-32 scroll-mt-16 space-y-28" data-testid="product-section">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8"><AICoach /></div>
    <div className="container mx-auto px-4 sm:px-6 lg:px-8"><Performance /></div>
    <div className="container mx-auto px-4 sm:px-6 lg:px-8"><MistakeBank /></div>
    <div className="container mx-auto px-4 sm:px-6 lg:px-8"><AdaptivePractice /></div>
    <div className="container mx-auto px-4 sm:px-6 lg:px-8"><ScoreTrajectory /></div>
  </section>
);
